import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MasterLayout from '../layouts/masterlayout';
import ErrorMessage from '../common/errormessage';
import Spinner from '../common/spinner';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '../../routes';
import { getHeaders, handleErrorResponse } from '../../lib/requests';
import PaginationControl from '../common/pagination_control';
import GeneralButton from '../common/button_general';
import TableHeader from '../common/table_header';
import GeneralizedTableRow from '../common/table_row';
import { EyeIcon, PencilSquareIcon, TrashIcon, PrinterIcon } from '@heroicons/react/24/outline';
import usePaginationAndSorting from '../../hooks/usepaginationandsorting';
import NoResultsFound from '../common/noresultsfound';
import DeliveryFilterComponent from '../common/filtering/delivery_filter_list';
import { formatDate, properCase, formatDateWithTime } from '../../lib/utils';
import { handlePdfFile } from '../../lib/utils';

function DeliveryList() {
    const { page, pageSize, sortBy, handlePageChange, handleSortClick } = usePaginationAndSorting('desc_scheduled_delivery_date');
    const navigate = useNavigate();
    const [deliveries, setDeliveries] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const searchQueryParam = queryParams.get('search');
    const routeNameQueryParam = queryParams.get('route_name');
    const clientQueryParam = queryParams.get('client_id');
    const afterDate = queryParams.get('scheduled_delivery_date_start');
    const beforeDate = queryParams.get('scheduled_delivery_date_end');
    const delivery_status = queryParams.get('delivery_status');

    const currentRoute = routes.find(route => route.path === location.pathname);
    const routePageTitle = currentRoute ? currentRoute.pageTitle : '';
    const [searchQuery] = useState(searchQueryParam || '');
    const [clientId, setClientId] = useState(clientQueryParam || '');

    const fetchDeliveries = async () => {
        setIsLoading(true);
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const apiToken = localStorage.getItem('accessToken');
            const response = await axios.get(`${apiUrl}/deliveries`, {
                headers: getHeaders(apiToken),
                params: {
                    page,
                    pageSize,
                    ...(sortBy && { order_by: sortBy }),
                    ...(searchQuery && { search: searchQuery }),
                    ...(routeNameQueryParam && { route_name: routeNameQueryParam }),
                    ...(clientId && { client_id: clientId }),
                    ...(afterDate && { after_date: afterDate }),
                    ...(beforeDate && { before_date: beforeDate }),
                    ...(delivery_status && { delivery_status: delivery_status })
                },
            });
            setTotalPages(response.data.totalPages);
            setTotalRecords(response.data.total);
            setDeliveries(response.data.data);
        } catch (error) {
            handleErrorResponse(error, navigate, setShowError, setErrorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDeliveries();
    }, [page, pageSize, sortBy, searchQuery, clientId, afterDate, beforeDate, delivery_status, routeNameQueryParam]);

    const handlePrintOrders = async (deliveryId) => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const apiToken = localStorage.getItem('accessToken');
            const response = await axios.get(`${apiUrl}/deliveries/${deliveryId}/orders/pdf`, {
                responseType: 'blob',
                headers: getHeaders(apiToken)
            });
            const fileName = `delivery_${deliveryId}_orders.pdf`;
            const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
            handlePdfFile(pdfBlob, fileName, 'print');
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    const handleDelete = async (deliveryId) => {
        const confirmed = window.confirm("Are you sure you want to delete this delivery?");
        if (!confirmed) return;
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const apiToken = localStorage.getItem('accessToken');
            await axios.delete(`${apiUrl}/deliveries/${deliveryId}`, {
                headers: getHeaders(apiToken)
            });
            fetchDeliveries();
        } catch (error) {
            if (
                error.response &&
                error.response.status === 400 &&
                error.response.data &&
                error.response.data.message
            ) {
                setErrorMessage(error.response.data.message);
                setShowError(true);
            } else {
                handleErrorResponse(error, navigate, setShowError, setErrorMessage);
            }
        }
    };

    const pageTitle = routePageTitle ? routePageTitle : 'Deliveries';

    const headerColumns = [
        { id: 'simple_id', name: 'ID' },
        { id: 'route_name', name: 'Route' },
        { id: 'client_name', name: 'Client' },
        { id: 'scheduled_delivery_date', name: 'Scheduled Delivery Date' },
        { id: 'delivery_date', name: 'Delivery Date' },
        { id: 'total_order_count', name: 'Orders' },
        { id: 'status', name: 'Status' },
        { id: 'create_date', name: 'Create Date' },
        { id: 'view', name: '' },
        { id: 'edit', name: '' },
        { id: 'delete', name: '' }
    ];

    const columns = [
        { id: 'simple_id', name: 'ID' },
        { id: 'route_name', name: 'Route', link: (rowData) => `/routes/${rowData.route_id}`, target: 'edit_route' },
        { id: 'client_name', name: 'Provider', link: (value) => `/providers/${value.client_id}`, target: 'edit_customer' },
        { id: 'scheduled_delivery_date', name: 'Scheduled Delivery Date', format: (value) => formatDate(value) },
        { id: 'delivery_date', name: 'Delivery Date', format: (value) => formatDate(value) },
        { id: 'total_order_count', name: 'Orders' },
        { id: 'status', name: 'Status', format: (value) => properCase(value) },
        { id: 'create_date', name: 'Create Date', format: (value) => formatDateWithTime(value) },
        { id: 'view', link: (rowData) => `/orders?delivery_id=${rowData.delivery_id}`, icon: EyeIcon, tooltip: 'View Orders' },
        { id: 'print_orders', action: (rowData) => handlePrintOrders(rowData.delivery_id), icon: PrinterIcon, tooltip: 'Print Orders' },
        { id: 'edit', link: (rowData) => `/deliveries/${rowData.delivery_id}`, icon: PencilSquareIcon, tooltip: 'Edit Delivery' },
        { id: 'delete', action: (rowData) => handleDelete(rowData.delivery_id), icon: TrashIcon, tooltip: 'Delete Delivery' }
    ];

    return (
        <MasterLayout pageTitle={pageTitle} searchQuery={searchQuery}>
            {showError && <ErrorMessage message={errorMessage} />}
            <div className="px-2 sm:px-3 lg:px-3">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-2 flex justify-between">
                    <h1 className="text-lg font-semibold text-gray-900">Deliveries</h1>
                    <GeneralButton type="submit" buttonType="primary" onClick={() => navigate('/deliveries/create')}>
                        Create Delivery
                    </GeneralButton>
                </div>
                <DeliveryFilterComponent onFilterChange={(client) => setClientId(client ? client.client_id : '')} />
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-2">
                    <table className="min-w-full divide-y divide-gray-300">
                        <TableHeader columns={headerColumns} handleSortClick={handleSortClick} currentSort={sortBy} />
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={headerColumns.length} className="text-center">
                                        <Spinner minHeight={630} />
                                    </td>
                                </tr>
                            ) : deliveries.length > 0 ? (
                                deliveries.map((delivery) => (
                                    <GeneralizedTableRow key={delivery.order_group_id} rowData={delivery} columns={columns} />
                                ))
                            ) : (
                                <NoResultsFound message={`No ${pageTitle.toLocaleLowerCase()} found.`} minHeight={630} columSpan={headerColumns.length} />
                            )}
                        </tbody>
                    </table>
                    <PaginationControl
                        page={page}
                        recordsPerPage={pageSize}
                        totalRecords={totalRecords}
                        totalPages={totalPages}
                        handlePageChange={handlePageChange}
                    />
                </div>
            </div>
        </MasterLayout>
    );
}

export default DeliveryList;
